import React, { useCallback, useMemo } from 'react';
import {
  TableRow,
  Checkbox,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Link,
  Button,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import * as state from '@/components/Analysis/state';
import { openTaskModal } from '../../RiskAssessment/state/atoms';
import Svg from 'react-inlinesvg';
import { ViewInAr as CubeIcon, History } from '@mui/icons-material';
import { useAtomValue, useSetAtom } from 'jotai';
import { Color, Columns } from '@/types';
import { TableData } from '@/types/table';

import { titleCaseSentence } from '@/utils';
import { abyssColors, primary } from '@/theme/colors';
import { useEquipmentData } from '@/hooks/useEquipmentData';
import { GridCheckCircleIcon } from '@mui/x-data-grid';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { PageLoader } from '@/components/shared/PageLoader';
import { VIEW_IN_3D_TOOLTIP_LABEL } from '@/constants';

type RowClickEvent = React.MouseEvent<HTMLTableRowElement, MouseEvent>;

type CustomTableCellProps<T extends TableData> = {
  value: string | boolean | number;
  label: string;
  columnIndex: number;
  isChecked?: boolean;
  color?: Color[];
  cellType?: string;
  onCheckboxChange: () => void;
  isSelected: boolean;
  unit: string | undefined;
  isUncontextualized?: boolean;
  item: T;
  subtitle?: string | undefined;
  onCellClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: T) => void;
};

const GOVERNING_BLISTER_VIEWPOINT_LABEL = 'GOVERNING BLISTER VIEWPOINT';

function CustomTableCell<T extends TableData>({
  value,
  columnIndex,
  isChecked,
  color,
  cellType,
  onCheckboxChange,
  isSelected,
  label,
  isUncontextualized,
  item,
  subtitle,
  onCellClick,
}: CustomTableCellProps<T>) {
  const structureLocations = useAtomValue(state.structureLocations);
  const setOpenTaskModal = useSetAtom(openTaskModal);

  let iconColor;
  if (color?.length) {
    iconColor = color.find((colorItem) => colorItem?.key === value)?.value;
  }
  const cellStyles = {
    backgroundColor: isSelected ? 'rgba(236,239,243,255)' : 'white',
    border: '1px solid rgba(224, 224, 224, 224)',
    whiteSpace: 'nowrap',
    padding: '8px',
    fontSize: '1.4rem',
    color: abyssColors.primary[500],
  } as React.CSSProperties;

  const foundViewpoint = useMemo(() => {
    if (label !== GOVERNING_BLISTER_VIEWPOINT_LABEL) {
      return undefined;
    }

    return structureLocations?.find((location) => location.id === value)?.name ?? 'N/A';
  }, [label, structureLocations, value]);

  const handleCellClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onCellClick) onCellClick(event, item);
  };

  const handleTaskClick = useCallback(() => {
    setOpenTaskModal(true);
  }, [setOpenTaskModal]);

  if (subtitle) {
    return (
      <TableCell style={{ ...cellStyles, width: 'auto' }}>
        {value}
        <Typography fontSize={'10px'} fontWeight={400} color="#7E9BBC" display={'inline'} ml={1}>
          ({subtitle})
        </Typography>
      </TableCell>
    );
  }

  if (label === GOVERNING_BLISTER_VIEWPOINT_LABEL) {
    return <TableCell style={cellStyles}>{foundViewpoint}</TableCell>;
  }

  switch (cellType) {
    case '3DBUTTON':
      return (
        <TableCell
          style={{
            ...cellStyles,
            position: 'sticky',
            right: 0,
            background: '#fff',
            zIndex: 1,
            boxShadow: '-4px 0px 4px 0px #0000000D',
            display: 'grid',
          }}
        >
          <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="top" arrow>
            <Button
              sx={{ p: { sm: '4px', xl: '9px' }, height: 'min-content' }}
              startIcon={
                <CubeIcon
                  sx={{
                    color: isUncontextualized
                      ? abyssColors.primary[300]
                      : abyssColors.secondary[600],
                    fontSize: '2rem !important',
                  }}
                />
              }
              onClick={handleCellClick}
              disabled={isUncontextualized}
            >
              3D
            </Button>
          </Tooltip>
        </TableCell>
      );
    case 'LINK':
      return (
        <TableCell style={cellStyles}>
          {!value || value === 'N/A' ? (
            value
          ) : (
            <Link href={`${window.location.origin}${value}`} target="_blank" rel="noreferrer">
              Link
            </Link>
          )}
        </TableCell>
      );
    case 'CHIP':
      return (
        <TableCell style={cellStyles}>
          <Chip
            sx={{
              backgroundColor: iconColor ?? abyssColors.neutral[400],
              height: { sm: '24px', xl: '32px' },
            }}
            label={titleCaseSentence(value)}
          />
        </TableCell>
      );
    case 'BULLET':
      return (
        <TableCell style={cellStyles}>
          <Stack direction="row" alignItems="baseline">
            <span
              style={{
                borderRadius: '50%',
                width: 8,
                height: 8,
                backgroundColor: iconColor ?? abyssColors.neutral[400],
                marginRight: '8px',
              }}
            />
            {value}
          </Stack>
        </TableCell>
      );
    case 'SQUIRCLE':
      return (
        <TableCell style={cellStyles}>
          <Stack direction="row" alignItems="baseline">
            <span
              style={{
                borderRadius: '0.2rem',
                width: '0.8rem',
                height: '0.8rem',
                backgroundColor: iconColor ?? abyssColors.neutral[400],
                marginRight: '0.8rem',
              }}
            />
            {value}
          </Stack>
        </TableCell>
      );
    case 'ARROW_UPWARD':
      return (
        <TableCell style={cellStyles}>
          <Stack direction="row" alignItems="center" gap="0.4rem">
            <SvgIcon
              sx={{
                color: iconColor ?? abyssColors.neutral[400],
                fontSize: '1.8rem',
              }}
            >
              <Svg src="/assets/icons/arrow-upward-rounded-icon.svg" />
            </SvgIcon>
            {value}
          </Stack>
        </TableCell>
      );
    case 'NOTE':
      return (
        <TableCell align="center" style={cellStyles}>
          <IconButton
            sx={{ p: { sm: '4px', xl: '9px' }, height: 'min-content' }}
            onClick={handleTaskClick}
          >
            <AbyssIcon iconName="description" />
          </IconButton>
        </TableCell>
      );
    case 'VERIFICATION':
      return (
        <TableCell style={cellStyles}>
          {value === 'true' ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <GridCheckCircleIcon sx={{ color: abyssColors.success[500] }} />
              <Typography>Verified</Typography>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <History sx={{ color: abyssColors.warning[600] }} />
              <Typography>Pending</Typography>
            </Stack>
          )}
        </TableCell>
      );
    default:
      return (
        <TableCell style={cellStyles}>
          {columnIndex === 0 ? (
            <Checkbox
              onChange={onCheckboxChange}
              checked={isChecked}
              sx={{ color: primary.light, p: { sm: '4px', xl: '9px' } }}
              disableRipple
            />
          ) : (
            <></>
          )}
          {value || 'N/A'}
        </TableCell>
      );
  }
}

type Props<T extends TableData> = {
  data: T[] | undefined;
  activeColumns: Columns[];
  onCheckboxChange: (id: string) => void;
  selectedIds: string[];
  unselectedIds?: string[];
  selectAll?: boolean;
  onRowClick: (event: RowClickEvent, item: T) => void;
  selectedRow: string | undefined;
  loading?: boolean;
};

export const CustomTableBody = <T extends TableData>({
  data,
  activeColumns,
  onCheckboxChange,
  selectAll = false,
  selectedIds = [],
  unselectedIds = [],
  onRowClick,
  selectedRow,
  loading,
}: Props<T>) => {
  const { getCellValue } = useEquipmentData();

  return (
    <>
      <TableBody sx={{ borderRadius: '0.5rem' }}>
        {loading && (
          <TableRow>
            <TableCell colSpan={activeColumns.length}>
              <PageLoader />
            </TableCell>
          </TableRow>
        )}
        {data?.map((item) => {
          return (
            <TableRow
              key={item.id}
              onClick={(event: RowClickEvent) => onRowClick(event, item)}
              sx={{ cursor: 'pointer' }}
            >
              {activeColumns.map((column, columnIndex) => {
                const {
                  columnName,
                  templateFieldKey,
                  displayConfig,
                  isTemplateField,
                  unit,
                  key,
                  onCellClick,
                } = column;
                const { value, subValue } = getCellValue(item, column);
                return (
                  <CustomTableCell
                    value={value}
                    subtitle={subValue}
                    key={`${item.id + key}`}
                    label={columnName}
                    columnIndex={columnIndex}
                    isChecked={
                      selectAll ? !unselectedIds.includes(item.id) : selectedIds.includes(item.id)
                    }
                    color={displayConfig?.color}
                    cellType={displayConfig?.type}
                    onCellClick={onCellClick}
                    onCheckboxChange={() => onCheckboxChange(item.id)}
                    isSelected={selectedRow === item.id}
                    isUncontextualized={!!item?.isUncontextualized}
                    item={item}
                    unit={
                      isTemplateField &&
                      templateFieldKey &&
                      item.templateFieldData?.[templateFieldKey] &&
                      unit
                        ? unit
                        : undefined
                    }
                  />
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </>
  );
};
